<template lang="pug">
  v-dialog(v-model="show"  persistent max-width="1000px"  )
    ControlResolveDialog(
      v-if="resolveTransactionData"
      @complete="onCloseButtonClick"
      :resolveTransactionData="resolveTransactionData"
      :isNextButton="isNextButton"
      :title="getTitle"
      @next="onNextButtonClick")
    v-form(ref="form" v-model="isFormValid" v-if="!resolveTransactionData")
      v-card.d-flex.flex-column(flat)
        v-card-title {{getTitle}}
        v-card-text
          v-row
            v-col(cols="6")
              v-text-field(outlined disabled
              v-model="fromIban"
              :label=" $t('bankTransactionPage.fromIbanColumnLabel')"
              )
            v-col(cols="6")
              v-text-field.amount(outlined disabled
              v-model="amount"
              type="number"
              :label="$t('bankTransactionPage.amountColumnLabel')"
              append-icon="fas fa-euro-sign"
              )
          v-row
            v-col(cols="6")
              v-text-field(outlined
              v-model="variableSymbol"
              type="number"
              :label="$t('bankTransactionPage.variableSymbolColumnLabel')"
              :variableSymbol="variableSymbolRules" )
            v-col(cols="6")
              v-text-field(outlined
              v-model="specificSymbol"
              type="number"
              :label="$t('bankTransactionPage.specificSymbolColumnLabel')"
              )
          v-row
            v-col(cols="12")
              v-text-field(
              outlined disabled
              v-model="name"
              :label="$t('bankTransactionPage.descriptionColumnLabel')"
              )
          v-row.ma-0
            v-checkbox(
            v-model="isIgnoreTransaction"
            :label="$t('bankTransactionPage.ignoreTransaction')"
             on-icon="fas fa-check-square" off-icon="fal fa-square"
            )
        v-divider
        v-card-actions
          v-btn(v-if="isSkipButton" color="orange" text  @click="onSkipTransaction") {{ $t('bankTransactionPage.skipTransaction') }}
          v-spacer
          v-btn(color="primary" text  @click="onCloseButtonClick") {{ $t('addBoarderDialog.closeButtonLabel') }}
          v-btn(color="primary"  @click="onSubmitButtonClick"  :disabled="!isFormValid" :loading="saveInProgress") {{ $t('bankTransactionPage.finish') }}


</template>

<script lang="ts">
import Vue from 'vue'
import {Component, Prop} from 'vue-property-decorator'
import i18n from "@/i18n";
import BankTransaction from "@/model/BankTransaction/BankTransaction";
import {Action} from "vuex-class";
import {RESOLVE_TRANSACTION} from "@/store/action-types";
import {BankTransactionResolvePayload} from "@/types/payload";
import ControlResolveDialog from "@/components/user/ControlResolveDialog.vue";

@Component({
  components: {
    ControlResolveDialog,
  },
})
export default class ResolveTransactionDialog extends Vue {
  $refs!: {
    form: Vue & {
      validate(): boolean
      resetValidation(): boolean
    }
  }
  @Prop({ type: Boolean, default: false }) readonly show!: boolean
  @Prop({ type: Array, required: false}) readonly currentSelection!: BankTransaction[]



  @Action(RESOLVE_TRANSACTION) resolveTransaction!: { (payload: BankTransactionResolvePayload): Promise<BankTransaction> }



  private variableSymbolRules: Array<(v: string) => string | boolean> = [
    (v: string) => !!v || i18n.t('validation.valueIsRequired').toString(),
    (v: string) => parseFloat(v) > 0 || i18n.t('validation.valueIsRequired').toString(),
    (v: string) => {
      const pattern = /^[0-9]$/
      return pattern.test(v) || i18n.t('validation.valueIsRequired\'').toString()
    }
  ]
  private isFormValid: boolean = true
  private saveInProgress: boolean = false
  private fromIban: string = ''
  private amount: string = ''
  private variableSymbol: string = ''
  private specificSymbol: string = ''
  private name: string = ''
  private transactionIndex: number = 0
  private resolveTransactionData: BankTransaction | null = null
  private isNextButton: boolean = true
  private completedTransactions: number[] = []
  private isIgnoreTransaction: boolean = false

  get isSkipButton (): boolean {
    if(this.currentSelection.length === this.transactionIndex + 1) {
      return false
    } else {
      return true
    }
  }

  get getTitle (): string {
    if(this.currentSelection) {
      return `${this.$t('bankTransactionPage.resolveTransactionLabel').toString()} ${this.transactionIndex + 1} / ${this.currentSelection.length}`
    } else {
      return this.$t('bankTransactionPage.resolveTransactionLabel').toString()
    }
  }

  onSkipTransaction(): void {
    this.resolveTransactionData = null
    this.transactionIndex += 1
    this.setValues()
  }

  onCloseButtonClick () {
    this.$emit('close', this.completedTransactions)
  }

  onSubmitButtonClick () {
    if (this.currentSelection.length === this.transactionIndex + 1) {
      this.isNextButton = false
    }
    this.toResolveTransaction()
  }

  onNextButtonClick (): void {
    this.resolveTransactionData = null
    this.transactionIndex += 1
    this.setValues()
    if (this.currentSelection.length === this.transactionIndex + 1) {
      this.isNextButton = false
    }
  }

  async toResolveTransaction (): Promise<void>{
    const payload: BankTransactionResolvePayload = {
      id: this.currentSelection[this.transactionIndex].transaction.id,
      variableSymbol: parseInt(this.variableSymbol),
      specificSymbol: this.specificSymbol ? parseInt(this.specificSymbol) : 0,
      status: this.isIgnoreTransaction ? 'ignored' : undefined
    }
    try {
      this.saveInProgress = true
     this.resolveTransactionData = await this.resolveTransaction(payload)
      this.completedTransactions.push(this.resolveTransactionData.transaction.id)
    }catch (e) {

    }finally {
      this.saveInProgress = false
    }
  }

  setValues (): void {
    this.fromIban = this.currentSelection[this.transactionIndex].transaction.fromIban
    this.amount = parseFloat((this.currentSelection[this.transactionIndex].transaction.amount / 100).toString()).toFixed(2).toString()
    this.variableSymbol = this.currentSelection[this.transactionIndex].transaction.variableSymbol.toString()
    this.specificSymbol = this.currentSelection[this.transactionIndex].transaction.specificSymbol > 0 ? this.currentSelection[this.transactionIndex].transaction.specificSymbol.toString() : ''
    this.name = this.currentSelection[this.transactionIndex].transaction.name !== 'not-provided' ? this.currentSelection[this.transactionIndex].transaction.name : ''
    this.isIgnoreTransaction = false
  }

  mounted() {
    this.setValues()
  }
}

</script>

<style lang="stylus" scoped>

.amount
  >>> .v-icon
    font-size 17px
    padding-bottom 5px

>>> input::-webkit-outer-spin-button
>>> input::-webkit-inner-spin-button
  -webkit-appearance none
  margin 0
</style>
